import { AfterViewChecked, ChangeDetectorRef, Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Store, select } from '@ngrx/store';
import { Subscription, Observable } from 'rxjs';

import * as MaintenanceReducer from "@app/maintenance/store/maintenance.reducer"
import {
    AddPlanAction,
    LoadPlanAction,
    LoadPlanToolsAction,
    UpdatePlanAction,
    DeleteMaintenancePlanAction,
    LoadTaskAction
} from '@app/maintenance/store/maintenance.actions';
import { TranslateService } from '@ngx-translate/core';
import { MaintenancePlanModalComponent } from '@app/maintenance/components/maintenance-plan-modal/maintenance-plan-modal.component';
import { ConfirmationModalComponent, ModalComponent } from '@app/shared/components';
import { NavigationService } from '@app/core/services/navigation.service';
import { Tool } from '@app/tool/model/tool.model';
import { ToolDetails } from '@app/tool/model/toolDetails.model';
import { TaskFormComponent } from '@app/maintenance/components/forms/task-form/task-form.component';
import { MaintenancePlanUtils } from '@app/shared/util/maintenance-plan.utils';
import { AuthenticationService } from '@app/authentication/services/authentication.service';
import { map } from "rxjs/operators";
import * as _ from "lodash";

@Component({
    selector: 'app-maintenance-plan-details',
    templateUrl: './maintenance-plan-details.component.html',
    styleUrls: ['./maintenance-plan-details.component.scss']
})
export class MaintenancePlanDetailsComponent implements OnInit, OnDestroy, AfterViewChecked {
    @ViewChild(MaintenancePlanModalComponent, {static: false}) modal: MaintenancePlanModalComponent;
    @ViewChild('versionConfirmationModal', {static: true}) confirmModal: ConfirmationModalComponent;
    @ViewChild('deleteConfirmationModal', {static: false}) deleteModal: ConfirmationModalComponent;
    @ViewChild('taskDetails', {static: false}) taskDetailsModal: ModalComponent;
    @ViewChild('taskForm', {static: false}) taskForm: TaskFormComponent;

    plan: any;
    planForUpdate: any;
    loading = false;
    isLoadingTools = false;
    isDuplicate = false;
    incrementVersion = false;
    tool: Tool;
    toolDetails: ToolDetails;
    taskRole: any;
    loadingTask = false;
    isReadOnly = false;

    plan$: Observable<any>;
    task$: Observable<any>;
    createdPlan$: Observable<any>;
    updatedPlan$: Observable<any>;
    planTools$: Observable<any>;
    loading$: Observable<any>;

    planSubscription: Subscription;
    taskSubscription: Subscription;
    createdPlanSubscription: Subscription;
    updatedPlanSubscription: Subscription;
    planToolsSubscription: Subscription;
    loadingSubscription: Subscription;

    constructor(private route: ActivatedRoute, private store: Store<any>, private translate: TranslateService,
                private cdr: ChangeDetectorRef, private router: Router, private navigationService: NavigationService,
                private authService: AuthenticationService) {
        this.plan$ = store.pipe(select(MaintenanceReducer.getPlan), map(plan => _.cloneDeep(plan)));
        this.task$ = store.pipe(select(MaintenanceReducer.getTask), map(task => _.cloneDeep(task)));
        this.createdPlan$ = store.pipe(select(MaintenanceReducer.getCreatedPlan), map(createdPlan => _.cloneDeep(createdPlan)));
        this.updatedPlan$ = store.pipe(select(MaintenanceReducer.getUpdatedPlan), map(updatedPlan => _.cloneDeep(updatedPlan)));
        this.loading$ = store.pipe(select(MaintenanceReducer.isLoading), map(loading => _.cloneDeep(loading)));
        this.planTools$ = store.pipe(select(MaintenanceReducer.getPlanTools), map(tools => _.cloneDeep(tools)));

        // Subscriptions
        this.planSubscription = this.plan$.subscribe(plan => {
            if (plan) {
                this.plan = {
                    ...plan,
                    items: MaintenancePlanUtils.buildPlanDetailsItems(
                        plan.maintenancePlanItems,
                        this.translate.instant(
                            'admin.toolMaintenancePlans.recurrences.recurringEvery')
                    )
                };
                this.isLoadingTools = true;
                store.dispatch(new LoadPlanToolsAction(this.plan.id));
                this.setReadonlyForUser();
            }
        });

        this.createdPlanSubscription = this.createdPlan$.subscribe(createdPlan => {
            if (createdPlan && createdPlan.id) {
                router.navigate([`../../${createdPlan.id}/details`], {relativeTo: route});
                navigationService.addToHistory(router.url);
            }
        });

        this.updatedPlanSubscription = this.updatedPlan$
            .subscribe(updatedPlan => {
                if (updatedPlan) {
                    if (this.plan) {
                        updatedPlan.tools = this.plan.tools;
                    }
                    this.plan = updatedPlan;
                    this.plan.items = MaintenancePlanUtils.buildPlanDetailsItems(
                        updatedPlan.maintenancePlanItems,
                        this.translate.instant(
                            'admin.toolMaintenancePlans.recurrences.recurringEvery')
                    );
                    this.isLoadingTools = true;
                    store.dispatch(new LoadPlanToolsAction(this.plan.id));
                }
            });

        this.planToolsSubscription = this.planTools$.subscribe(tools => {
            if (tools && this.plan) {
                this.plan.tools = tools;
                this.isLoadingTools = false;
            }
        });

        this.loadingSubscription = this.loading$.subscribe(loading => {
            this.loading = loading;
            if (!loading) {
                this.loadingTask = false;
            }
        });

        this.taskSubscription = this.task$.subscribe(task => {
            if (task) {
                this.taskRole = {
                    id: task.assignedRoleId,
                    icon: "fa fa-user",
                    title: task.assignedRole.name,
                    subtitle: this.translate.instant(
                        `admin.toolMaintenancePlans.activities.modal.${task.assignedRole.isOob ? 'implicitRole' : 'personalizedRole'}`
                    ),
                    showAssign: false
                }
                setTimeout(() => this.taskForm.init(task));
            }
        });
    }

    ngOnInit() {
        this.plan = null;
        if (this.router.url.split("/")[1].includes('tool-maintenance-plans')) {
            this.route.paramMap.subscribe(params => {
                this.store.dispatch(new LoadPlanAction(parseInt(params.get('id'), 10)));
            });
        }
    }

    ngAfterViewChecked(): void {
        this.cdr.detectChanges();
    }

    onEdit(event: any) {
        this.modal.open(_.cloneDeep(event));
    }

    setReadonlyForUser() {
        const currentUser = this.authService.getUserDetails();
        if ((currentUser.permissions.includes('PROCESS_TICKETS') ||
                currentUser.permissions.includes('MANAGE_TICKETS')) &&
            !currentUser.permissions.includes('MANAGE_MAINTENANCE_PLANS')) {
            this.isReadOnly = true;
        }
    }

    canViewMaintenancePlanDetails(): boolean {
        return this.authService.hasPermission('VIEW_MAINTENANCE_PLANS');
    }

    handleEditPlan(event: any) {
        const eventClone = _.cloneDeep(event);
        if (this.isDuplicate) {
            this.store.dispatch(new AddPlanAction(eventClone));
            this.isDuplicate = false;
        } else {
            this.planForUpdate = eventClone;
            this.confirmModal.open();
        }
    }

    onDuplicate(event: any) {
        this.modal.open({...event, name: event.name + " - Copy"});
        this.isDuplicate = true;
    }

    onEditPlan() {
        this.confirmModal.close();
        this.store.dispatch(new UpdatePlanAction(this.planForUpdate, this.incrementVersion));
    }

    goToPlan(event: any) {
        if (event.id) {
            this.router.navigate([`/tool-maintenance-plans/plans/${event.id}/details`]);
            this.navigationService.addToHistory(this.router.url);
        }
    }

    onTaskClicked(task: any) {
        this.taskDetailsModal.open();
        this.loadingTask = true;
        this.store.dispatch(new LoadTaskAction(task.id));
    }

    closeTaskModal() {
        this.taskDetailsModal.close();
    }

    onDelete() {
        this.deleteModal.open();
    }

    onDeletePlan() {
        this.deleteModal.close();
        this.store.dispatch(new DeleteMaintenancePlanAction(_.cloneDeep(this.plan)));
    }

    setToolDetails(toolDetails: any) {
        this.toolDetails = toolDetails
        if (this.toolDetails && this.toolDetails.maintenancePlanId) {
            this.store.dispatch(new LoadPlanAction(this.toolDetails.maintenancePlanId));
        }
    }

    setTool(tool: any) {
        this.tool = tool;
    }

    ngOnDestroy() {
        if (this.planSubscription) {
            this.planSubscription.unsubscribe();
        }

        if (this.createdPlanSubscription) {
            this.createdPlanSubscription.unsubscribe();
        }

        if (this.updatedPlanSubscription) {
            this.updatedPlanSubscription.unsubscribe();
        }

        if (this.planToolsSubscription) {
            this.planToolsSubscription.unsubscribe();
        }

        if (this.loadingSubscription) {
            this.loadingSubscription.unsubscribe();
        }

        if (this.taskSubscription) {
            this.taskSubscription.unsubscribe();
        }
    }

}
